// Componente de filtro de búsqueda con texto visible
import React, { useState } from 'react'
import { formatDateDisplay, parseLocalDateString } from '../../utils/dateHelpers'
import { useTheme } from '../../hooks/useTheme'
import { useTiposPublicos } from '../../hooks/useTiposPublicos'
import type { ConsultaDisponibilidad } from '../../types'

interface SearchFilterProps {
  onSearch: (consulta: ConsultaDisponibilidad) => void
  onClear: () => void
  currentQuery?: ConsultaDisponibilidad | null
  className?: string
  hideTitle?: boolean  // Ocultar título "Encuentra el espacio..."
}

const SearchFilter: React.FC<SearchFilterProps> = ({
  onSearch,
  onClear,
  currentQuery,
  className = '',
  hideTitle = false
}) => {
  const { colors } = useTheme()
  const { data: tipos = [], isLoading: tiposLoading, error: tiposError } = useTiposPublicos()
  const [fechaEntrada, setFechaEntrada] = useState(currentQuery?.fecha_entrada || '')
  const [fechaSalida, setFechaSalida] = useState(currentQuery?.fecha_salida || '')
  const [tipoEspacio, setTipoEspacio] = useState('')

  // Obtener fecha mínima (hoy)
  const today = new Date().toISOString().split('T')[0]

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!fechaEntrada || !fechaSalida) {
      alert('Por favor selecciona las fechas de entrada y salida')
      return
    }

    if (parseLocalDateString(fechaEntrada) > parseLocalDateString(fechaSalida)) {
      alert('La fecha de salida debe ser posterior o igual a la fecha de entrada')
      return
    }

    const consulta: ConsultaDisponibilidad = {
      fecha_entrada: fechaEntrada,
      fecha_salida: fechaSalida,
      tipo_espacio_id: tipoEspacio || undefined
    }

    onSearch(consulta)
  }

  const handleClear = () => {
    setFechaEntrada('')
    setFechaSalida('')
    setTipoEspacio('')
    onClear()
  }

  return (
    <div className={`bg-white/95 backdrop-blur-sm shadow-2xl border border-gray-200 p-8 rounded-3xl ${className}`}>
      {!hideTitle && (
        <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
          Encuentra el espacio perfecto para tu familia
        </h3>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Fecha de entrada */}
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Fecha Inicio
            </label>
            <input
              type="date"
              value={fechaEntrada}
              onChange={(e) => setFechaEntrada(e.target.value)}
              min={today}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-transparent bg-white text-gray-900"
              style={{
                '--tw-ring-color': colors.primary
              } as React.CSSProperties}
              onFocus={(e) => {
                e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.primary}20, 0 0 0 2px ${colors.primary}`
              }}
              onBlur={(e) => {
                e.currentTarget.style.boxShadow = 'none'
              }}
              required
            />
          </div>

          {/* Fecha de salida */}
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Fecha Término
            </label>
            <input
              type="date"
              value={fechaSalida}
              onChange={(e) => setFechaSalida(e.target.value)}
              min={fechaEntrada || today}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-transparent bg-white text-gray-900"
              style={{
                '--tw-ring-color': colors.primary
              } as React.CSSProperties}
              onFocus={(e) => {
                e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.primary}20, 0 0 0 2px ${colors.primary}`
              }}
              onBlur={(e) => {
                e.currentTarget.style.boxShadow = 'none'
              }}
              required
            />
          </div>

          {/* Tipo de espacio (opcional) */}
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Tipo de espacio
            </label>
            <select
              value={tipoEspacio}
              onChange={(e) => setTipoEspacio(e.target.value)}
              disabled={tiposLoading}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-transparent bg-white text-gray-900 disabled:opacity-50"
              style={{
                '--tw-ring-color': colors.primary
              } as React.CSSProperties}
              onFocus={(e) => {
                e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.primary}20, 0 0 0 2px ${colors.primary}`
              }}
              onBlur={(e) => {
                e.currentTarget.style.boxShadow = 'none'
              }}
            >
              <option value="">
                {tiposLoading ? 'Cargando tipos...' : tipos.length === 0 ? 'No hay tipos disponibles' : 'Todos los tipos'}
              </option>
              {tipos.filter(tipo => tipo.reservable !== false).map((tipo) => (
                <option key={tipo.id} value={tipo.id}>
                  {tipo.nombre}
                </option>
              ))}
            </select>
            {tiposError && (
              <p className="text-red-600 text-xs mt-1">
                Error al cargar tipos: {tiposError instanceof Error ? tiposError.message : 'Error desconocido'}
              </p>
            )}
          </div>
        </div>
        
        {/* Botones de acción */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
          <button
            type="submit"
            style={{
              backgroundColor: colors.primary,
              color: '#FFFFFF'
            }}
            className="font-semibold px-8 py-3 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.primaryDark
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.primary
            }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span>Buscar espacios</span>
          </button>
          
          {currentQuery && (
            <button
              type="button"
              onClick={handleClear}
              style={{
                borderWidth: '2px',
                borderColor: colors.primary,
                color: colors.primary,
                backgroundColor: 'transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `${colors.primary}10`
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent'
              }}
              className="font-semibold px-8 py-3 rounded-lg transition-all duration-200 hover:scale-105 flex items-center justify-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span>Limpiar Filtros</span>
            </button>
          )}
        </div>
      </form>

      {/* Información de búsqueda actual */}
      {currentQuery && (
        <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
          <p className="text-blue-800 text-sm text-center">
            🔍 Mostrando espacios disponibles del{' '}
            <span className="font-semibold">
              {formatDateDisplay(currentQuery.fecha_entrada)}
            </span>
            {' '}al{' '}
            <span className="font-semibold">
              {formatDateDisplay(currentQuery.fecha_salida)}
            </span>
          </p>
        </div>
      )}
    </div>
  )
}

export default SearchFilter
